import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { BreedingCreaturePopup } from '../popups/breeding/breeding-creature.popup';
import { ListForSaleCreaturePopup } from '../popups/list-for-sale/list-for-sale-creature.popup';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CreaturesService } from '../../../../services/creatures.service';
import { CreatureWebDto } from '../../../../dtos/creature.web.dto';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RarityType } from '../../../../enums/rarity.type';
import { RarityNamePipe } from '../../../../pipes/rarity-name.pipe';
import { CreatureTypePipe } from '../../../../pipes/creature-type.pipe';
import { Web3WalletService } from '../../../../services/web3-wallet.service';
import { ethers } from 'ethers';
import { MarketplaceTradeListingService } from '../../../../services/marketplace-trade-listing.service';
import { MarketplaceTradeListingDto } from '../../../../dtos/marketplace-trade-listing.dto';
import { TimeAgoPipe } from '../../../../pipes/time-ago.pipe';
import { CreatureLiteWebDto } from '../../../../dtos/creature.lite.web.dto';
import { ItemCreatureComponent } from '../list/components/item/item-creature.component';
import { AbilityDamageType } from '../../../../dtos/ability.dto';
import { EthPricePipe } from '../../../../pipes/eth-price.pipe';
import { CreatureSortType } from '../../../../enums/creature-sort.type';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { MarketplaceTradePurchaseStatus } from '../../../../enums/marketplace-trade-purchase.status';
import { CountdownPipe } from '../../../../pipes/countdown.pipe';
import { DateToSecondsLeftPipe } from '../../../../pipes/date-to-seconds-left.pipe';
import { UserService } from '../../../../services/user.service';

var window: Window & { ethereum: any } = globalThis as any;

@Component({
    selector: 'view-creature-page',
    templateUrl: './view-creature.page.html',
    styleUrl: './view-creature.page.scss',
    imports: [
        CommonModule,
        RouterLink,
        RarityNamePipe,
        TimeAgoPipe,
        CreatureTypePipe,
        BreedingCreaturePopup,
        ListForSaleCreaturePopup,
        EthPricePipe,
        ItemCreatureComponent,
        TranslateModule,
        CountdownPipe,
        DateToSecondsLeftPipe
    ]
})
export class ViewCreaturePage implements OnInit, AfterViewInit {

    public blockchain: string = environment.blockchain;;

    public lang: string | null = null;

    public showBreedingPopup: boolean = false;
    public showListForSalePopup: boolean = false;

    public creature?: CreatureWebDto | null = undefined;
    public isWalletConnected: boolean = false;
    public walletAddress: string | null = null;

    public salesHistory: MarketplaceTradeListingDto[] | undefined = undefined;

    public meIsOwner: boolean | undefined = undefined;
    public showLoading: boolean = false;

    public rarityType: typeof RarityType = RarityType;
    public abilityDamageType: typeof AbilityDamageType = AbilityDamageType;
    public marketplaceTradePurchaseStatus: typeof MarketplaceTradePurchaseStatus = MarketplaceTradePurchaseStatus;

    public relatedCreatures: CreatureLiteWebDto[] = [];
    public parentCreatures: CreatureLiteWebDto[] = [];
    public childCreatures: CreatureLiteWebDto[] = [];

    // Animation flag toggled after view init (browser only) to trigger CSS transition
    public animateIn: boolean = false;

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private readonly activatedRoute: ActivatedRoute,
        private readonly creaturesService: CreaturesService,
        private readonly web3WalletService: Web3WalletService,
        private readonly marketplaceTradeListingService: MarketplaceTradeListingService,
        private readonly translateService: TranslateService,
        private readonly cdr: ChangeDetectorRef,
        private readonly userService: UserService
    ) {
        if (isPlatformBrowser(this.platformId)) {
            this.web3WalletService.onChangeUser.subscribe((user) => {
                this.isWalletConnected = this.web3WalletService.isWalletConnected();
                this.walletAddress = user ? user.walletAddress! : null;
                this.meIsOwner = this.creature?.ownerAddress?.toLowerCase() === this.walletAddress?.toLowerCase();
                if (user) {
                    this.cdr.markForCheck();
                }
            });
        }
        this.lang = this.translateService.currentLang;
        this.translateService.onLangChange.subscribe((event) => {
            this.lang = event.lang;
        })
    }

    private loadCreature(id: string) {
        this.creaturesService.getById(id).subscribe({
            next: (data) => {
                this.creature = data;

                // if (this.creature && this.creature.nextBreedingAt) {
                //     const nextBreedingAt = new Date(this.creature.nextBreedingAt);
                //     const now = new Date();
                //     if (now > nextBreedingAt) {
                //         this.creature.nextBreedingAt = null;
                //         this.creature.isAllowForBreeding = true;
                //     }
                // }

                if (isPlatformBrowser(this.platformId)) {
                    this.meIsOwner = this.creature?.ownerAddress?.toLowerCase() === this.walletAddress?.toLowerCase();

                    this.marketplaceTradeListingService.getByFilter({
                        nftTokenId: this.creature!.nftTokenId!,
                        page: 1,
                        pageSize: 100,
                    }).subscribe({
                        next: (data) => {
                            this.salesHistory = data;
                        },
                        error: (err) => {
                            console.error('Failed to load creatures:', err);
                        },
                    });

                    this.creaturesService.getByFilter({
                        availableForBreeding: null,
                        abilityIds: null,
                        maxHp: null,
                        minHp: null,
                        minLevel: null,
                        maxLevel: null,
                        maxPrice: null,
                        minPrice: null,
                        onlyMy: false,
                        onlyForTrade: true,
                        isChildren: false,
                        isParent: false,
                        rarities: [this.creature.rarity],
                        types: [this.creature.type],
                        ignoreIds: [this.creature.id],
                        nftTokenIds: [],
                        nftParentTokenId: null,
                        page: 0,
                        pageSize: 6,
                        sortBy: CreatureSortType.PriceAsc
                    }).subscribe({
                        next: (data) => {
                            this.relatedCreatures = data;
                        },
                        error: (err) => {
                            console.error('Failed to load creatures:', err);
                        },
                    });

                    this.creaturesService.getByFilter({
                        availableForBreeding: null,
                        abilityIds: null,
                        maxHp: null,
                        minHp: null,
                        minLevel: null,
                        maxLevel: null,
                        maxPrice: null,
                        minPrice: null,
                        onlyMy: false,
                        onlyForTrade: false,
                        isChildren: false,
                        isParent: false,
                        rarities: [],
                        types: [],
                        ignoreIds: [],
                        nftTokenIds: [],
                        nftParentTokenId: this.creature.nftTokenId,
                        page: 0,
                        pageSize: 6,
                        sortBy: CreatureSortType.PriceAsc
                    }).subscribe({
                        next: (data) => {
                            this.childCreatures = data;
                        },
                        error: (err) => {
                            console.error('Failed to load creatures:', err);
                        },
                    });

                    if (this.creature.breedingParents && this.creature.breedingParents.length > 0) {
                        this.creaturesService.getByFilter({
                            availableForBreeding: null,
                            abilityIds: null,
                            maxHp: null,
                            minHp: null,
                            minLevel: null,
                            maxLevel: null,
                            maxPrice: null,
                            minPrice: null,
                            onlyMy: false,
                            onlyForTrade: false,
                            rarities: null,
                            types: null,
                            isChildren: false,
                            isParent: false,
                            ignoreIds: [this.creature.id],
                            nftTokenIds: [...this.creature.breedingParents],
                            nftParentTokenId: null,
                            page: 0,
                            pageSize: 6,
                            sortBy: CreatureSortType.PriceAsc
                        }).subscribe({
                            next: (data) => {
                                this.parentCreatures = data;
                            },
                            error: (err) => {
                                console.error('Failed to load creatures:', err);
                            },
                        });
                    }

                } else {
                    this.meIsOwner = undefined;
                }
            },
            error: (err) => {
                console.error('Failed to load creatures:', err);
            },
        });
    }

    ngOnInit(): void {
        let id = this.activatedRoute.snapshot.paramMap.get('id');
        if (id) {
            this.loadCreature(id);
        } else {
            console.error('No id provided in the route');
        }
    }

    ngAfterViewInit(): void {
        // Trigger enter animation only in browser (avoid SSR mismatch)
        if (isPlatformBrowser(this.platformId)) {
            // Next tick so initial class renders first
            setTimeout(() => this.animateIn = true, 0);
        }
    }

    async onBuy($event: Event) {
        try {
            this.showLoading = true;

            const adminAddress = this.web3WalletService.getAdminAddress();
            const myAddress = this.web3WalletService.getWalletAddress()!;

            const contract: any = await this.web3WalletService.getCoinContract();

            const existingAllowance = await contract.allowance(myAddress, adminAddress);
            const allowanceValue = existingAllowance ? ethers.formatUnits(existingAllowance, 'ether') : 0;

            const price = this.creature!.tradeListing!.price!;
            const fee = price * 0.05;

            const wei = ethers.parseUnits((fee + price + parseFloat(allowanceValue || '0')).toFixed(8).toString(), 'ether');

            const feeData = await this.web3WalletService.getFeeData();
            const gasPriceInWei = ethers.formatUnits(feeData.gasPrice!, 'wei');

            const estimatedGas = await this.web3WalletService.providerRef!.estimateGas({
                to: contract.target,
                from: myAddress,
                data: contract.interface.encodeFunctionData('approve', [adminAddress, wei]),
            });

            const tx = await contract.approve(adminAddress, wei, {
                gasPrice: gasPriceInWei,
                gasLimit: estimatedGas
            });

            const receipt = await tx.wait();

            this.marketplaceTradeListingService.purchaseListing(this.creature!.id, {
                myAddress: myAddress,
                transactionHash: tx.hash,
            }).subscribe({
                next: (data) => {
                    this.creature!.tradeListing!.purchaseStatus = MarketplaceTradePurchaseStatus.Pending;
                    this.showLoading = false;
                    const oldOwner = this.creature!.ownerAddress;
                    let timerRef: any = null;
                    timerRef = setInterval(() => {
                        this.loadCreature(this.creature!.id);
                        if (this.creature?.ownerAddress?.toLowerCase() !== oldOwner?.toLowerCase()) {
                            clearInterval(timerRef);
                        }
                    }, 1000 * 10);
                },
                error: (err) => {
                    console.error('Error creating listing:', err);
                    this.showLoading = false;
                },
            });
        } catch (error) {
            console.error('Error executing approve function:', error);
            this.showLoading = false;
        }
    }

}
